import {
  BlobServiceClient,
  BlockBlobClient,
  ContainerClient,
} from "@azure/storage-blob";
import {
  getDownloadSignedUrl,
  getSignedUrl,
  getWorkspaceLibraryDocumentDownloadUrl,
} from "api";
import { getWorkspaceDocumentDownloadUrl } from "apps/project-iq/api";
import imageCompression from "browser-image-compression";
import { ConstantValues } from "common/constants";
import { FileWithId } from "models/FileWithId";
import { IFile } from "models/IFile";
import { IWorkspaceLibraryDocument } from "components/manageWorkspaces/workspaceLibrary/workspaceLibrary.types";

export class InvalidUploadFileError extends Error {
  constructor(message: string) {
    super(message);
    // Not required, but makes uncaught error messages nicer.
    this.name = "InvalidUploadFileError";
  }
}

const IMAGE_EXTENSIONS = [
  "png",
  "jpg",
  "jpeg",
  "webp",
  "bmp",
  "tiff",
  "tif",
  "gif",
];

const COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 2000,
  useWebWorker: true,
  fileType: "image/jpeg" as const,
  initialQuality: 0.85,
};

const isImageFile = (filename: string): boolean => {
  const extension = filename.split(".").pop()?.toLowerCase();
  return extension ? IMAGE_EXTENSIONS.includes(extension) : false;
};

const compressIfImage = async (file: File): Promise<File> => {
  if (!isImageFile(file.name)) {
    return file;
  }

  try {
    console.log(
      `Compressing image: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`,
    );
    const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS);

    const originalSizeMB = file.size / 1024 / 1024;
    const compressedSizeMB = compressedFile.size / 1024 / 1024;
    const reductionPercent = (
      (1 - compressedSizeMB / originalSizeMB) *
      100
    ).toFixed(1);

    console.log(
      `Compressed ${file.name}: ${originalSizeMB.toFixed(2)}MB → ${compressedSizeMB.toFixed(2)}MB ` +
        `(${reductionPercent}% reduction)`,
    );

    const originalNameWithoutExt = file.name.substring(
      0,
      file.name.lastIndexOf("."),
    );
    const newName =
      isImageFile(file.name) &&
      !file.name.toLowerCase().endsWith(".jpg") &&
      !file.name.toLowerCase().endsWith(".jpeg")
        ? `${originalNameWithoutExt}.jpg`
        : file.name;

    return new File([compressedFile], newName, { type: "image/jpeg" });
  } catch (error) {
    console.error(`Failed to compress image ${file.name}:`, error);
    return file;
  }
};

export const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      resolve(base64);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error("Failed to read file"));
    };
    reader.readAsDataURL(file);
  });
};

export const prepareHeaderBytes = async (processedFile: File) => {
  // Read first 5MB of file for validation
  let fileData: string | undefined;
  try {
    const MAX_SIZE = 2 * 1024 * 1024; // 5MB
    const fileSlice = processedFile.slice(
      0,
      Math.min(processedFile.size, MAX_SIZE),
    );

    // Convert to base64
    const reader = new FileReader();
    const readPromise = new Promise<string>((resolve, reject) => {
      reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1]; // Remove data:type;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
    });
    reader.readAsDataURL(fileSlice);
    fileData = await readPromise;
  } catch (error) {
    console.warn("Failed to read file for validation:", error);
    // Continue without file data - backend will handle accordingly
  }
  return fileData;
};

export const uploadFilesToStorage = async (
  uploadFiles: FileWithId[],
  entityType: string,
  entityId: string,
  isTemporary: boolean = false,
  conversationId?: string,
  folderPath?: string,
): Promise<IFile[]> => {
  const uploadedFiles: IFile[] = [];

  for (let i = 0; i < uploadFiles.length; i++) {
    const fileObject: FileWithId = uploadFiles[i];

    const isDuplicate: boolean = uploadedFiles.some(
      (file: IFile): boolean => file.filename === fileObject?.file?.name,
    );
    if (isDuplicate) {
      continue;
    }

    const processedFile = await compressIfImage(fileObject.file);

    const originalFileName: string = processedFile.name;
    let encodedFileName: string;
    if (isTemporary) {
      const fileExtension =
        originalFileName.split(".").pop() || ConstantValues.EMPTY_STRING;
      const baseFileName = originalFileName.split(".").slice(0, -1).join(".");
      const tempName = fileExtension
        ? `${baseFileName}_temporary.${fileExtension}`
        : `${baseFileName}_temporary`;
      encodedFileName = encodeURIComponent(
        tempName.replace(/^\/+/, "").replace(/\/{2,}/g, "/"),
      );
    } else {
      encodedFileName = encodeURIComponent(
        originalFileName.replace(/^\/+/, "").replace(/\/{2,}/g, "/"),
      );
    }

    const fileData = await prepareHeaderBytes(processedFile);

    const uploadFileSASData = await getSignedUrl({
      filename: encodedFileName,
      entityType: entityType,
      entityId: entityId,
      conversationId: conversationId,
      fileData,
      folderPath,
    });

    if (uploadFileSASData?.error) {
      throw new InvalidUploadFileError("file-not-in-allowed-list");
    }

    const { sas, containerName, blobName } = uploadFileSASData;

    uploadedFiles.push({
      id: fileObject.id ?? undefined,
      url: blobName.replace(/\/{2,}/g, "/"),
      filename: processedFile.name.replace(/\/{2,}/g, "/"),
    });

    const blobServiceClient: BlobServiceClient = new BlobServiceClient(sas);
    const containerClient: ContainerClient =
      blobServiceClient.getContainerClient(containerName);
    const blockBlobClient: BlockBlobClient =
      containerClient.getBlockBlobClient(blobName);

    await blockBlobClient.uploadData(processedFile, {
      blockSize: 4 * 1024 * 1024,
      concurrency: 20,
    });
  }

  return uploadedFiles;
};

export const uploadLogoToStorage = async (
  file: File,
  entityType: "diagnostics-logo" | "project-iq-workspace-logo",
  entityId: string,
): Promise<string> => {
  const encodedFileName = encodeURIComponent(
    file.name.replace(/^\/+/, "").replace(/\/{2,}/g, "/"),
  );
  const fileData = await prepareHeaderBytes(file);

  const { sas, containerName, blobName } = await getSignedUrl({
    filename: encodedFileName,
    entityType,
    entityId,
    conversationId: undefined,
    fileData,
    folderPath: undefined,
  });

  const blobServiceClient = new BlobServiceClient(sas);
  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blockBlobClient = containerClient.getBlockBlobClient(blobName);

  await blockBlobClient.uploadData(file, {
    blockSize: 4 * 1024 * 1024,
    concurrency: 20,
    blobHTTPHeaders: {
      blobContentType: file.type || "application/octet-stream",
    },
  });

  return blobName.replace(/\/{2,}/g, "/");
};

export const downloadFile = (fileUrl: string) => async () => {
  try {
    const fileWithUrl = await getDownloadSignedUrl({ filename: fileUrl });
    window.open(fileWithUrl?.sas, "_blank");
  } catch (error) {
    console.error("Error downloading file from Blob storage:", error);
  }
  return undefined;
};

const sanitizeDownloadFilename = (filename: string): string =>
  filename.replace(/[/\\:*?"<>|]/g, "_");

export const fetchBlobFromSasUrl = async (sasUrl: string): Promise<Blob> => {
  const response = await fetch(sasUrl);
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }
  return await response.blob();
};

export const triggerBrowserDownload = (
  blob: Blob,
  downloadFilename: string,
): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = sanitizeDownloadFilename(downloadFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadBlobAsFile = async (
  blobPath: string,
  downloadFilename: string,
): Promise<void> => {
  const fileWithUrl = await getDownloadSignedUrl({ filename: blobPath });
  if (!fileWithUrl?.sas) {
    throw new Error("Failed to get download URL");
  }

  const blob = await fetchBlobFromSasUrl(fileWithUrl.sas);
  triggerBrowserDownload(blob, downloadFilename);
};

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  ".csv": "text/csv",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

const resolveMimeTypeFromFilename = (
  filename: string,
  fallback: string,
): string => {
  const extension = filename.slice(filename.lastIndexOf(".")).toLowerCase();
  return MIME_TYPE_BY_EXTENSION[extension] ?? fallback;
};

const fetchSasBlob = async (fileWithUrl?: { sas?: string }): Promise<Blob> => {
  if (!fileWithUrl?.sas) {
    throw new Error("Failed to get download URL");
  }
  return fetchBlobFromSasUrl(fileWithUrl.sas);
};

export const fetchProjectIqWorkspaceDocumentAsFile = async (
  workspaceId: string,
  doc: IWorkspaceLibraryDocument,
): Promise<File> => {
  const blob = await fetchSasBlob(
    await getWorkspaceDocumentDownloadUrl({
      workspaceId,
      filename: doc.filename,
    }),
  );
  return new File([blob], doc.originalFilename, {
    type: resolveMimeTypeFromFilename(doc.originalFilename, blob.type),
  });
};

export const downloadProjectIqWorkspaceDocument = async (
  workspaceId: string,
  doc: IWorkspaceLibraryDocument,
): Promise<void> => {
  const blob = await fetchSasBlob(
    await getWorkspaceDocumentDownloadUrl({
      workspaceId,
      filename: doc.filename,
    }),
  );
  triggerBrowserDownload(blob, doc.originalFilename);
};

export const downloadWorkspaceLibraryDocument = async (
  workspaceId: string,
  doc: IWorkspaceLibraryDocument,
): Promise<void> => {
  const blob = await fetchSasBlob(
    await getWorkspaceLibraryDocumentDownloadUrl({
      workspaceId,
      filename: doc.filename,
    }),
  );
  triggerBrowserDownload(blob, doc.originalFilename);
};

export const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};
